import React, { useMemo } from "react";

import { Button, Icon, Label, Modal } from "semantic-ui-react";

import moment from "moment";
import { RowInfo } from "react-table-6";

import { Table } from "react-lib/frameworks/Table";

import {
  aggregateSlotInfo,
  calculateSlotInfo,
  DSBDetailType,
  GroupedScheduleEventType,
  ProviderDetailType,
} from "./sequence/ScheduleSetting";

import { formatDate } from "react-lib/utils/dateUtils";

const IMAGES = {
  calendar: "/static/images/schedule/calendar-grey-icon.png",
  chat: "/static/images/schedule/chat-grey-icon.png",
  mobile: "/static/images/schedule/mobile-icon.png",
  provider: "/static/images/schedule/provider-black-icon.png",
};

export type ModScheduleAppointmentProps = {
  divisionId: number;
  event: GroupedScheduleEventType;
  open: boolean;
  providers: ProviderDetailType[];
  onClose: () => void;
  onSelectChat?: (hn: string) => void;
  onSelectProvider?: (data: DSBDetailType) => void;
};

const ModScheduleAppointment = (props: ModScheduleAppointmentProps) => {
  const isAppointmentView = props.providers.length > 0;

  const aggregatedSlotInfo = useMemo(() => aggregateSlotInfo(props.providers), [props.providers]);

  const uniqueProviders = useMemo(() => {
    if (isAppointmentView) {
      return [];
    }

    const providerMap = new Map<number, Record<string, any>>();

    for (const item of props.event.items) {
      for (const provider of item.providers) {
        if (!providerMap.has(provider.doctor_provider_id)) {
          providerMap.set(provider.doctor_provider_id, provider);
        }
      }
    }

    return [...providerMap.values()];
  }, [isAppointmentView, props.event]);

  const appointmentItems = useMemo(() => {
    if (!isAppointmentView) {
      return [];
    }

    const appointments = props.providers.flatMap((provider) =>
      provider.dsb.appointments.map((app) => ({ ...app, division_id: provider.dsb.division_id }))
    );

    return appointments.map((app) => {
      const date = moment(app.estimated_at);
      const startTime = date.format("HH:mm");
      const endTime = date.clone().add(app.estimated_duration, "minutes").format("HH:mm");
      const fullName = `${app.first_name} ${app.last_name}`;

      return {
        ...app,
        chat: (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {props.divisionId === app.division_id && (
              <img
                style={{ cursor: "pointer", width: "20px" }}
                alt="chat.icon"
                src={IMAGES.chat}
                onClick={() => {
                  props.onSelectChat?.(app.hn);
                }}
              />
            )}
          </div>
        ),
        date: formatDate(date),
        editUserName: (
          <div style={{ alignItems: "center", display: "flex" }}>
            {app.edit_user_name === fullName ? (
              <img
                style={{ marginRight: "0.5rem", width: "9px" }}
                alt="mobile.icon"
                src={IMAGES.mobile}
              />
            ) : (
              <div style={{ marginRight: "0.5rem", width: "9px" }} />
            )}
            <label>{app.edit_user_name}</label>
          </div>
        ),
        endTime: (
          <div
            style={{
              color: app.type_code === "APP_INSERT" ? "var(--primary-theme-color)" : "",
              textAlign: "center",
            }}
          >
            {endTime}
          </div>
        ),
        fullName: (
          <div>
            {`${app.first_name} ${app.last_name}`}
            {app.type_code === "APP_INSERT" && (
              <Label
                style={{
                  border: "1px solid var(--primary-theme-color)",
                  borderRadius: "25px",
                  color: "var(--primary-theme-color)",
                  fontSize: "0.7rem",
                  marginLeft: "0.75rem",
                  padding: ".45em 1.25em",
                }}
                basic
              >
                คิวแทรก
              </Label>
            )}
          </div>
        ),
        startTime: (
          <div
            style={{
              color: app.type_code === "APP_INSERT" ? "var(--primary-theme-color)" : "",
              textAlign: "center",
            }}
          >
            {startTime}
          </div>
        ),
      };
    });
  }, [props.divisionId, props.providers]);

  const providerItems = useMemo(
    () =>
      uniqueProviders.map((provider) => {
        const data = provider.dsb as Record<string, any>;
        const info = calculateSlotInfo(data);

        return {
          ...data,
          endTime: <div style={{ textAlign: "center" }}>{data.doctor_end_time}</div>,
          schedule: (
            <div style={{ textAlign: "center" }}>
              {info.scheduledCount} / {info.totalSlots}
            </div>
          ),
          slotLength: <div style={{ textAlign: "center" }}>{data.doctor_dsb_slot_length}</div>,
          startTime: <div style={{ textAlign: "center" }}>{data.doctor_start_time}</div>,
        };
      }),
    [uniqueProviders]
  );

  return (
    <Modal
      style={{ border: "rgba(243, 130, 160, 1)", borderRadius: "6px", width: "75rem" }}
      open={props.open}
      closeOnDimmerClick
      onClose={props.onClose}
    >
      <div style={{ padding: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Icon
            style={{ color: "rgba(79, 79, 79, 1)", fontSize: "1.15rem", marginBottom: "-0.75rem" }}
            name="close"
            link
            onClick={props.onClose}
          />
        </div>
        <div style={{ alignItems: "center", display: "flex", marginTop: "0.5rem" }}>
          <Label>
            <label>{props.event.full_date}</label>
            <label style={{ paddingLeft: "2rem" }}>{props.event.full_title}</label>
          </Label>

          {isAppointmentView ? (
            <>
              <img
                style={{ margin: "0 0.5rem 0 3rem", width: "17px" }}
                alt="provider.icon"
                src={IMAGES.provider}
              />
              <Label>{props.providers[0]?.dsb.doctor_provider_name}</Label>
              <img
                style={{ margin: "0 0.5rem 0 3rem", width: "17px" }}
                alt="calendar.icon"
                src={IMAGES.calendar}
              />
              <Label>{aggregatedSlotInfo.statusText}</Label>
            </>
          ) : (
            <>
              <img
                style={{ marginLeft: "3rem", width: "17px" }}
                alt="provider.icon"
                src={IMAGES.provider}
              />
              <label style={{ padding: "0 0.5rem 0 0.5rem" }}>ผู้ให้บริการ</label>
              <Label>{uniqueProviders.length}</Label>
              <label style={{ paddingLeft: "0.5rem" }}>คน</label>
            </>
          )}
        </div>
        {isAppointmentView ? (
          <Table
            style={{ marginTop: "0.75rem", maxHeight: "400px", minHeight: "200px" }}
            data={appointmentItems}
            headers="Booking ID,Member Name,Chat,Date,Start time,End time,Booked by"
            keys="id,fullName,chat,date,startTime,endTime,editUserName"
            minRows={4}
            showPagination={false}
            widths="110,^120,90,110,110,110,^120"
            getTheadTrProps={() => ({
              style: {
                backgroundColor: "rgba(255, 212, 223, 1)",
                color: "rgba(117, 117, 117, 1)",
              },
            })}
          />
        ) : (
          <Table
            style={{ marginTop: "0.75rem", maxHeight: "400px", minHeight: "200px" }}
            data={providerItems}
            headers="Provider,Specialty,Start time,End time,Service Time / Slot,Schedule/All Slot"
            keys="doctor_provider_name,specialty,startTime,endTime,slotLength,schedule"
            minRows={4}
            showPagination={false}
            widths="^90,^120,110,110,140,150"
            getTheadTrProps={() => ({
              style: {
                backgroundColor: "rgba(255, 212, 223, 1)",
                color: "rgba(117, 117, 117, 1)",
              },
            })}
            getTrProps={(state: any, row: RowInfo) => ({
              style: {
                cursor: "pointer",
                transition: "color 0.2s ease",
              },
              onClick: () => {
                props.onSelectProvider?.(row.original);
              },
              onMouseEnter: (e: React.MouseEvent<HTMLTableCellElement>) => {
                e.currentTarget.style.color = "rgba(229, 67, 110, 1)";
              },
              onMouseLeave: (e: React.MouseEvent<HTMLTableCellElement>) => {
                e.currentTarget.style.color = "";
              },
            })}
          />
        )}
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
          <Button color="grey" size="small" onClick={props.onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ModScheduleAppointment.displayName = "ModScheduleAppointment";

export default React.memo(ModScheduleAppointment);
